/**
 * Unified Permissions Hook Factory
 * 
 * Purpose: DRY pattern for Global ⊗ Project permission checks
 * Replaces 30+ files with duplicate permission logic
 * 
 * Architecture:
 * - useGate(): Factory that enforces AND logic (Global AND Project)
 * - Resource-specific hooks: Convenience wrappers for common patterns
 * 
 * Usage:
 * ```tsx
 * const { allowed, isLoading } = useCanEditItemsInProject(projectId);
 * if (!allowed) return <AccessDenied />;
 * ```
 */

import { useMemo } from 'react';
import { useConsolidatedPermissions } from './useConsolidatedPermissions';
import { useProjectPermissions } from './useProjectPermissions';

/**
 * Core gate factory - enforces AND logic for all permission checks
 * 
 * @param globalPermission - Global permission flag from consolidated permissions
 * @param projectId - Project ID to check project-level permissions
 * @param action - 'view' | 'edit' | 'delete'
 * @returns { allowed: boolean, isLoading: boolean }
 */
export const useGate = (
  globalPermission: boolean,
  projectId: string | undefined,
  action: 'view' | 'edit' | 'delete'
) => {
  const { data: projectPerms, isLoading: projectLoading } = useProjectPermissions(projectId || '');
  
  return useMemo(() => {
    if (!projectId) {
      return { allowed: false, isLoading: false };
    }

    const projectAllowed = 
      action === 'view' ? projectPerms?.can_view :
      action === 'edit' ? projectPerms?.can_edit :
      projectPerms?.can_delete;

    // ✅ CRITICAL: AND logic enforced here
    const allowed = globalPermission && (projectAllowed || false);

    return {
      allowed,
      isLoading: projectLoading
    };
  }, [globalPermission, projectPerms, projectLoading, projectId, action]);
};

// ============================================================================
// ITEMS PERMISSIONS
// ============================================================================

/**
 * Check if user can ACCESS items feature in a project
 * Global: ITEMS_ACCESS OR ITEMS_VIEW_IN_PROJECT
 * Project: can_view = true
 */
export const useCanAccessItemsInProject = (projectId: string | undefined) => {
  const { data: perms, isLoading: globalLoading } = useConsolidatedPermissions();
  
  // Fallback to ITEMS_ACCESS from RPC when canAccessItems is not available
  const hasGlobal = perms?.canAccessItems || perms?.ITEMS_ACCESS || false;
  const gate = useGate(hasGlobal, projectId, 'view');
  
  return {
    allowed: gate.allowed,
    isLoading: globalLoading || gate.isLoading
  };
};

/**
 * Check if user can EDIT items in a project
 * Global: ITEMS_EDIT_BASIC
 * Project: can_edit = true
 */
export const useCanEditItemsInProject = (projectId: string | undefined) => {
  const { data: perms, isLoading: globalLoading } = useConsolidatedPermissions();
  
  const hasGlobal = perms?.ITEMS_EDIT_BASIC || false;
  const gate = useGate(hasGlobal, projectId, 'edit');
  
  return {
    allowed: gate.allowed,
    isLoading: globalLoading || gate.isLoading
  };
};

/**
 * Check if user can DELETE items in a project
 * Global: ITEMS_DELETE
 * Project: can_delete = true
 */
export const useCanDeleteItemsInProject = (projectId: string | undefined) => {
  const { data: perms, isLoading: globalLoading } = useConsolidatedPermissions();
  
  const hasGlobal = perms?.ITEMS_DELETE || false;
  const gate = useGate(hasGlobal, projectId, 'delete');
  
  return {
    allowed: gate.allowed,
    isLoading: globalLoading || gate.isLoading
  };
};

// ============================================================================
// SPECS PERMISSIONS
// ============================================================================

/**
 * Check if user can ACCESS specs feature in a project
 * Global: SPECS_ACCESS OR SPECS_VIEW_ITEMS
 * Project: can_view = true
 */
export const useCanAccessSpecsInProject = (projectId: string | undefined) => {
  const { data: perms, isLoading: globalLoading } = useConsolidatedPermissions();
  
  const hasGlobal = perms?.canAccessSpecs || perms?.SPECS_ACCESS || false;
  const gate = useGate(hasGlobal, projectId, 'view');
  
  return {
    allowed: gate.allowed,
    isLoading: globalLoading || gate.isLoading
  };
};

/**
 * Check if user can EDIT specs in a project
 * Global: SPECS_EDIT_ITEMS
 * Project: can_edit = true
 */
export const useCanEditSpecsInProject = (projectId: string | undefined) => {
  const { data: perms, isLoading: globalLoading } = useConsolidatedPermissions();
  
  const hasGlobal = perms?.SPECS_ITEMS_EDIT || false;
  const gate = useGate(hasGlobal, projectId, 'edit');
  
  return {
    allowed: gate.allowed,
    isLoading: globalLoading || gate.isLoading
  };
};

/**
 * Check if user can DELETE specs in a project
 * Global: SPECS_DELETE_PINS OR SPECS_ITEMS_DELETE
 * Project: can_delete = true
 */
export const useCanDeleteSpecsInProject = (projectId: string | undefined) => {
  const { data: perms, isLoading: globalLoading } = useConsolidatedPermissions();
  
  const hasGlobal = perms?.SPECS_DELETE_PINS || false;
  const gate = useGate(hasGlobal, projectId, 'delete');
  
  return {
    allowed: gate.allowed,
    isLoading: globalLoading || gate.isLoading
  };
};

// ============================================================================
// PROCUREMENT PERMISSIONS
// ============================================================================

/**
 * Check if user can ACCESS procurement feature in a project
 * Global: PROCUREMENT_ACCESS OR canAccessProcurement
 * Project: can_view = true
 */
export const useCanAccessProcurementInProject = (projectId: string | undefined) => {
  const { data: perms, isLoading: globalLoading } = useConsolidatedPermissions();
  
  const hasGlobal = perms?.canAccessProcurement || false;
  const gate = useGate(hasGlobal, projectId, 'view');
  
  return {
    allowed: gate.allowed,
    isLoading: globalLoading || gate.isLoading
  };
};

/**
 * Check if user can CREATE procurement orders in a project
 * Global: PROCUREMENT_CREATE_ORDER
 * Project: can_edit = true
 */
export const useCanCreateProcurementInProject = (projectId: string | undefined) => {
  const { data: perms, isLoading: globalLoading } = useConsolidatedPermissions();
  
  const hasGlobal = perms?.PROCUREMENT_CREATE_ORDER || false;
  const gate = useGate(hasGlobal, projectId, 'edit');
  
  return {
    allowed: gate.allowed,
    isLoading: globalLoading || gate.isLoading
  };
};

/**
 * Check if user can EDIT procurement orders in a project
 * Global: PROCUREMENT_EDIT_ORDER
 * Project: can_edit = true
 */
export const useCanEditProcurementInProject = (projectId: string | undefined) => {
  const { data: perms, isLoading: globalLoading } = useConsolidatedPermissions();
  
  const hasGlobal = perms?.PROCUREMENT_EDIT_ORDER || false;
  const gate = useGate(hasGlobal, projectId, 'edit');
  
  return {
    allowed: gate.allowed,
    isLoading: globalLoading || gate.isLoading
  };
};

/**
 * Check if user can DELETE procurement orders in a project
 * Global: PROCUREMENT_DELETE_ORDER
 * Project: can_delete = true
 */
export const useCanDeleteProcurementInProject = (projectId: string | undefined) => {
  const { data: perms, isLoading: globalLoading } = useConsolidatedPermissions();
  
  const hasGlobal = perms?.PROCUREMENT_DELETE_ORDER || false;
  const gate = useGate(hasGlobal, projectId, 'delete');
  
  return {
    allowed: gate.allowed,
    isLoading: globalLoading || gate.isLoading
  };
};

// ============================================================================
// SUBCONS PERMISSIONS
// ============================================================================

/**
 * Check if user can ACCESS subcons feature in a project
 * Global: SUBCONS_ACCESS OR canAccessSubcons
 * Project: can_view = true
 */
export const useCanAccessSubconsInProject = (projectId: string | undefined) => {
  const { data: perms, isLoading: globalLoading } = useConsolidatedPermissions();
  
  const hasGlobal = perms?.canAccessSubcons || perms?.SUBCONS_ACCESS || false;
  const gate = useGate(hasGlobal, projectId, 'view');
  
  return {
    allowed: gate.allowed,
    isLoading: globalLoading || gate.isLoading
  };
};

/**
 * Check if user can EDIT subcons in a project
 * Global: SUBCONS_EDIT
 * Project: can_edit = true
 */
export const useCanEditSubconsInProject = (projectId: string | undefined) => {
  const { data: perms, isLoading: globalLoading } = useConsolidatedPermissions();
  
  const hasGlobal = perms?.SUBCONS_EDIT || false;
  const gate = useGate(hasGlobal, projectId, 'edit');
  
  return {
    allowed: gate.allowed,
    isLoading: globalLoading || gate.isLoading
  };
};

/**
 * Check if user can MANAGE payment milestones in a project
 * Global: SUBCONS_PAYMENT_MANAGE
 * Project: can_edit = true
 */
export const useCanManagePaymentsInProject = (projectId: string | undefined) => {
  const { data: perms, isLoading: globalLoading } = useConsolidatedPermissions();
  
  const hasGlobal = perms?.SUBCONS_MANAGE || false;
  const gate = useGate(hasGlobal, projectId, 'edit');
  
  return {
    allowed: gate.allowed,
    isLoading: globalLoading || gate.isLoading
  };
};

/**
 * Check if user can DELETE subcons in a project
 * Global: SUBCONS_MANAGE (fallback as SUBCONS_DELETE might not exist yet)
 * Project: can_delete = true
 */
export const useCanDeleteSubconsInProject = (projectId: string | undefined) => {
  const { data: perms, isLoading: globalLoading } = useConsolidatedPermissions();
  
  // Fallback to SUBCONS_MANAGE if SUBCONS_DELETE doesn't exist
  const hasGlobal = perms?.SUBCONS_MANAGE || false;
  const gate = useGate(hasGlobal, projectId, 'delete');
  
  return {
    allowed: gate.allowed,
    isLoading: globalLoading || gate.isLoading
  };
};

// ============================================================================
// STAKEHOLDERS PERMISSIONS
// ============================================================================

/**
 * Check if user can ACCESS stakeholders feature in a project
 * Global: STAKEHOLDERS_ACCESS OR canAccessStakeholders
 * Project: can_view = true
 */
export const useCanAccessStakeholdersInProject = (projectId: string | undefined) => {
  const { data: perms, isLoading: globalLoading } = useConsolidatedPermissions();
  
  const hasGlobal = perms?.canAccessStakeholders || false;
  const gate = useGate(hasGlobal, projectId, 'view');
  
  return {
    allowed: gate.allowed,
    isLoading: globalLoading || gate.isLoading
  };
};

/**
 * Check if user can EDIT stakeholders in a project
 * Global: STAKEHOLDERS_EDIT
 * Project: can_edit = true
 */
export const useCanEditStakeholdersInProject = (projectId: string | undefined) => {
  const { data: perms, isLoading: globalLoading } = useConsolidatedPermissions();
  
  // Note: Consolidated permissions might not have STAKEHOLDERS_EDIT yet
  // Fallback to canAccessStakeholders for now
  const hasGlobal = perms?.canAccessStakeholders || false;
  const gate = useGate(hasGlobal, projectId, 'edit');
  
  return {
    allowed: gate.allowed,
    isLoading: globalLoading || gate.isLoading
  };
};

/**
 * Check if user can DELETE stakeholders in a project
 * Global: STAKEHOLDERS_DELETE
 * Project: can_delete = true
 */
export const useCanDeleteStakeholdersInProject = (projectId: string | undefined) => {
  const { data: perms, isLoading: globalLoading } = useConsolidatedPermissions();
  
  // Note: Consolidated permissions might not have STAKEHOLDERS_DELETE yet
  // Fallback to canAccessStakeholders for now
  const hasGlobal = perms?.canAccessStakeholders || false;
  const gate = useGate(hasGlobal, projectId, 'delete');
  
  return {
    allowed: gate.allowed,
    isLoading: globalLoading || gate.isLoading
  };
}
